import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Eye, History, Play, RefreshCw, Repeat } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import {
  ACTION_LABELS,
  STATUS_LABELS,
  rowHref,
  type DisplayStatus,
  type SupplierQuestionnaireRow,
} from './rows';

interface SupplierQuestionnaireTableProps {
  rows: SupplierQuestionnaireRow[];
  onShowHistory: (row: SupplierQuestionnaireRow) => void;
}

/** Badge tones for the supplier-facing statuses. */
const STATUS_TONE: Record<DisplayStatus, string> = {
  not_started: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  in_progress: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  submitted: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  completed: 'bg-green-100 text-green-800 hover:bg-green-100',
  more_info_required: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  rejected: 'bg-red-100 text-red-800 hover:bg-red-100',
  expired: 'bg-gray-200 text-gray-700 hover:bg-gray-200',
};

const dash = '—';
const date = (iso: string | null) => (iso ? formatDate(iso) : dash);

/** The single primary verb per row, or "View responses" once the supplier's part is done. */
function PrimaryAction({ row, onGo }: { row: SupplierQuestionnaireRow; onGo: () => void }) {
  if (row.action === 'none') {
    return (
      <Button type="button" size="sm" variant="outline" onClick={onGo}>
        <Eye className="h-4 w-4 mr-1.5" />
        View responses
      </Button>
    );
  }
  const Icon = row.action === 'renew' ? RefreshCw : Play;
  return (
    <Button type="button" size="sm" onClick={onGo}>
      <Icon className="h-4 w-4 mr-1.5" />
      {ACTION_LABELS[row.action]}
    </Button>
  );
}

/**
 * CR-5865: the supplier's merged questionnaire table.
 *
 * One row per assignment, whichever system it came from. Columns follow the
 * ticket: name, type, status, due date, completion date, score, result,
 * required action, last updated. Score and result stay blank until the row's
 * `showScore` rule allows them.
 */
export function SupplierQuestionnaireTable({ rows, onShowHistory }: SupplierQuestionnaireTableProps) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 border-b">
          <tr>
            <th className="p-3 text-left font-semibold">Questionnaire</th>
            <th className="p-3 text-left font-semibold">Type</th>
            <th className="p-3 text-left font-semibold">Status</th>
            <th className="p-3 text-left font-semibold">Due date</th>
            <th className="p-3 text-left font-semibold">Completed</th>
            <th className="p-3 text-center font-semibold">Score</th>
            <th className="p-3 text-center font-semibold">Result</th>
            <th className="p-3 text-left font-semibold">Required action</th>
            <th className="p-3 text-left font-semibold">Last updated</th>
            <th className="p-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b hover:bg-muted/30" data-testid={`questionnaire-row-${row.key}`}>
              <td className="p-3">
                <div className="font-medium">{row.name}</div>
                {row.customerName && (
                  <div className="text-xs text-muted-foreground">{row.customerName}</div>
                )}
              </td>
              <td className="p-3">
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline">{row.typeLabel}</Badge>
                  {row.recurring && (
                    <Badge variant="outline" className="gap-1 text-muted-foreground" title="Recurring questionnaire">
                      <Repeat className="h-3 w-3" /> Recurring
                    </Badge>
                  )}
                </div>
              </td>
              <td className="p-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge className={STATUS_TONE[row.status]}>{STATUS_LABELS[row.status]}</Badge>
                  {row.isOverdue && <Badge variant="destructive">Overdue</Badge>}
                </div>
                {row.feedback && (
                  <div className="text-xs text-muted-foreground mt-1 max-w-xs truncate" title={row.feedback}>
                    {row.feedback}
                  </div>
                )}
              </td>
              <td className={`p-3 whitespace-nowrap ${row.isOverdue ? 'text-red-600 font-medium' : ''}`}>
                {date(row.dueDate)}
              </td>
              <td className="p-3 whitespace-nowrap">{date(row.submittedAt)}</td>
              <td className="p-3 text-center whitespace-nowrap">
                {row.showScore && row.scorePercentage != null
                  ? `${Math.round(row.scorePercentage)}%`
                  : <span className="text-muted-foreground">{dash}</span>}
              </td>
              <td className="p-3 text-center">
                {row.showScore && row.passed != null ? (
                  <Badge className={row.passed
                    ? 'bg-green-100 text-green-800 hover:bg-green-100'
                    : 'bg-red-100 text-red-800 hover:bg-red-100'}>
                    {row.passed ? 'Passed' : 'Failed'}
                  </Badge>
                ) : <span className="text-muted-foreground">{dash}</span>}
              </td>
              <td className="p-3 whitespace-nowrap">
                {row.action === 'none'
                  ? <span className="text-muted-foreground">None</span>
                  : <span className="font-medium">{ACTION_LABELS[row.action]}</span>}
                {row.action === 'renew' && row.daysUntilExpiry != null && (
                  <div className="text-xs text-muted-foreground">
                    {row.daysUntilExpiry < 0 ? 'Expired' : `Renew in ${row.daysUntilExpiry}d`}
                  </div>
                )}
              </td>
              <td className="p-3 whitespace-nowrap text-muted-foreground">{date(row.updatedAt)}</td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-2">
                  <PrimaryAction row={row} onGo={() => navigate(rowHref(row))} />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onShowHistory(row)}
                    aria-label={`History for ${row.name}`}
                  >
                    <History className="h-4 w-4 mr-1.5" />
                    History
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
